import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { methodologyService, GuidedMethodology, GuidedMethodologyStep, auth, db, COLLECTIONS } from "../../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { MethodologyVisualization } from '../../components/ui/methodology-visualization';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

interface GuidedMethodologyPageParams {
  methodologyId: string;
}

interface GuidedMethodologyPageProps {
  forcedMethodologyId?: string;
}

export const GuidedMethodologyPage: React.FC<GuidedMethodologyPageProps> = ({ forcedMethodologyId }) => {
  const { methodologyId: routeMethodologyId } = useParams<keyof GuidedMethodologyPageParams>() as GuidedMethodologyPageParams;
  const methodologyId = forcedMethodologyId || routeMethodologyId;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a new session from the URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isNewSession = searchParams.get('newSession') === 'true';
  
  // Add debug logging for the newSession parameter
  console.log(`GuidedMethodologyPage - isNewSession: ${isNewSession}, search: ${location.search}`);
  
  // Add a ref to track if we've already reset progress for this session
  const progressResetRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodology, setMethodology] = useState<GuidedMethodology | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userInput, setUserInput] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const [userName, setUserName] = useState<string>("");
  const [targetDomain, setTargetDomain] = useState<string>("");
  const authInstance = getAuth();
  const [viewMode, setViewMode] = useState<'step'>('step');

  // Function to navigate with preserving newSession parameter
  const navigateWithSessionParam = (path: string) => {
    if (isNewSession) {
      navigate(`${path}?newSession=true`);
    } else {
      navigate(path);
    }
  };

  // Authentication effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
      if (user) {
        console.log(`User authenticated in GuidedMethodologyPage: ${user.uid}`);
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
        
        // Ensure user progress is properly synchronized
        try {
          await methodologyService.ensureUserProgressSync();
        } catch (error) {
          console.error("Error syncing user progress:", error);
        }
      } else {
        console.log("User not authenticated, redirecting to signin");
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load methodology and user progress
  useEffect(() => {
    const loadMethodologyAndProgress = async () => {
      setLoading(true);
      
      try {
        // Load the methodology
        const data = await methodologyService.getGuidedMethodologyById(methodologyId);
        if (!data) {
          setError("Methodology not found");
          setLoading(false);
          return;
        }
        
        if (data.status !== 'Active') {
          setError("This methodology is not currently available");
          setLoading(false);
          return;
        }
        
        setMethodology(data);
        
        // Skip loading previous progress if this is a new session
        if (!isNewSession) {
          // Try to load user progress
          await loadUserProgress(data);
        } else {
          console.log("Starting new session - skipping previous progress");
          // Reset progress for new session
          setCurrentStepIndex(0);
          setCompletedStepIds([]);
          setProgress(0);
          
          // Save initial state to track this new session
          const sessionData = {
            methodologyId,
            type: 'guided',
            completedSteps: [],
            lastStepIndex: 0,
            updatedAt: new Date().toISOString()
          };
          
          // Save to user's session data
          await methodologyService.saveUserProgress(methodologyId, 'guided', sessionData);
          
          // Mark that we've reset progress for this session
          progressResetRef.current = true;
        }
      } catch (error) {
        console.error("Error loading guided methodology:", error);
        setError("Failed to load guided methodology");
      } finally {
        setLoading(false);
      }
    };

    loadMethodologyAndProgress();
  }, [methodologyId, isNewSession]);
  
  // Load user's saved progress
  const loadUserProgress = async (methodology: GuidedMethodology) => {
    if (!auth || !auth.currentUser || !db) return;
    
    try {
      const userId = auth.currentUser.uid;
      const userProgressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      const userProgressSnap = await getDoc(userProgressRef);
      
      if (userProgressSnap.exists()) {
        const userData = userProgressSnap.data();
        
        // Check if there's saved progress for this specific methodology
        if (userData.guidedMethodology && 
            userData.guidedMethodology.methodologyId === methodologyId &&
            userData.guidedMethodology.completedSteps) {
          
          console.log("Restoring user progress...");
          
          // Restore completed steps
          setCompletedStepIds(userData.guidedMethodology.completedSteps);
          
          // Restore current step index
          if (userData.guidedMethodology.lastStepIndex !== undefined) {
            const lastIndex = userData.guidedMethodology.lastStepIndex;
            
            // Make sure the index is valid
            if (lastIndex >= 0 && lastIndex < methodology.steps.length) {
              setCurrentStepIndex(lastIndex);
            }
          }
          
          // Calculate progress
          const progressPercentage = (userData.guidedMethodology.completedSteps.length / methodology.steps.length) * 100;
          setProgress(progressPercentage);
          
          console.log("User progress restored successfully");
        }
      }
    } catch (error) {
      console.error("Error loading user progress:", error);
    }
  };

  // Update progress when completed steps change
  useEffect(() => {
    if (methodology && methodology.steps.length > 0) {
      const progressPercentage = (completedStepIds.length / methodology.steps.length) * 100;
      setProgress(progressPercentage);
      
      // Only save progress if this is not a new session or if we have completed steps
      // This prevents overwriting the "new session" state when the component first loads
      if (!isNewSession || completedStepIds.length > 0) {
        saveUserProgress();
      }
    }
  }, [completedStepIds, methodology, currentStepIndex, isNewSession]);
  
  // Save user progress for session continuation
  const saveUserProgress = async () => {
    if (!methodology || !auth || !auth.currentUser) return;
    
    try {
      // Create a session record in the format expected by findMethodologiesWithCompletedItems
      const sessionData = {
        methodologyId,
        type: 'guided',
        completedSteps: completedStepIds,
        lastStepIndex: currentStepIndex,
        updatedAt: new Date().toISOString()
      };
      
      // Save to user's session data
      await methodologyService.saveUserProgress(methodologyId, 'guided', sessionData);
      
      console.log("Progress saved for session continuation");
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  const handleNextStep = () => {
    if (!methodology) return;
    
    const currentStep = methodology.steps[currentStepIndex];
    
    // Mark current step as completed
    if (!completedStepIds.includes(currentStep.id)) {
      setCompletedStepIds(prev => [...prev, currentStep.id]);
    }
    
    // Determine next step based on flow type
    if (currentStep.nextStepType === 'Linear') {
      if (currentStep.nextStepId) {
        // Find index of the next step
        const nextIndex = methodology.steps.findIndex(step => step.id === currentStep.nextStepId);
        if (nextIndex !== -1) {
          setCurrentStepIndex(nextIndex);
          setUserInput('');
          return;
        }
      }
      
      // If no valid next step ID or it's the last step
      if (currentStepIndex < methodology.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } else if (currentStep.nextStepType === 'Conditional' && currentStep.conditions) {
      // Find matching condition
      for (const condition of currentStep.conditions) {
        if (matchesCondition(userInput, condition.condition, condition.value)) {
          if (condition.nextStepId === 'end') {
            // End of methodology
            setCurrentStepIndex(methodology.steps.length);
          } else {
            // Find index of the next step
            const nextIndex = methodology.steps.findIndex(step => step.id === condition.nextStepId);
            if (nextIndex !== -1) {
              setCurrentStepIndex(nextIndex);
              setUserInput('');
              return;
            }
          }
        }
      }
      
      // If no condition matches, go to next step
      if (currentStepIndex < methodology.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } else {
      // Default behavior: go to next step
      if (currentStepIndex < methodology.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    }
    
    setUserInput('');
    
    // Explicitly save progress when user advances to next step
    saveUserProgress();
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setUserInput('');
      
      // Explicitly save progress when user goes back to previous step
      saveUserProgress();
    }
  };

  const matchesCondition = (input: string, condition: string, value: string): boolean => {
    const normalizedInput = input.trim().toLowerCase();
    const normalizedValue = value.trim().toLowerCase();
    
    switch (condition.toLowerCase()) {
      case 'contains':
        return normalizedInput.includes(normalizedValue);
      case 'equals':
        return normalizedInput === normalizedValue;
      case 'startswith':
        return normalizedInput.startsWith(normalizedValue);
      case 'endswith':
        return normalizedInput.endsWith(normalizedValue);
      case 'greater than':
        return parseFloat(normalizedInput) > parseFloat(normalizedValue);
      case 'less than':
        return parseFloat(normalizedInput) < parseFloat(normalizedValue);
      default:
        return false;
    }
  };

  const renderCurrentStep = () => {
    if (!methodology || !methodology.steps[currentStepIndex]) {
      if (currentStepIndex >= methodology?.steps.length) {
        // End of methodology
        return (
          <div className="text-center py-8">
            <h3 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-4">
              Methodology Complete
            </h3>
            <p className="mb-6">
              You have completed all steps in this guided methodology.
            </p>
            <Button
              onClick={() => navigateWithSessionParam(`/guided-methodologies`)}
              className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
            >
              Back to Methodologies
            </Button>
          </div>
        );
      }
      
      return (
        <div className="text-center py-8 text-gray-500">
          <p>Step information not available</p>
        </div>
      );
    }

    const currentStep = methodology.steps[currentStepIndex];
    
    return (
      <div className="space-y-6">
        <div>
          <h3 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-2">
            Step {currentStepIndex + 1}: {currentStep.title}
          </h3>
          <p className="text-sm text-black opacity-80 whitespace-pre-wrap">
            {currentStep.description}
          </p>
        </div>
        
        {currentStep.toolSuggestions.length > 0 && (
          <div>
            <h4 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
              Suggested Tools
            </h4>
            <div className="flex flex-wrap gap-2">
              {currentStep.toolSuggestions.map((tool, index) => {
                const toolUrl = currentStep.toolSuggestionUrls?.[tool];
                
                if (toolUrl) {
                  return (
                    <a 
                      key={index}
                      href={toolUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-3 py-1 bg-gray-100 rounded-full text-sm hover:bg-gray-200 transition-colors flex items-center gap-1"
                    >
                      {tool}
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  );
                }
                
                return (
                  <span 
                    key={index}
                    className="inline-block px-3 py-1 bg-gray-100 rounded-full text-sm"
                  >
                    {tool}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        
        {currentStep.commandExamples.length > 0 && (
          <div>
            <h4 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
              Example Commands
            </h4>
            <div className="space-y-2">
              {currentStep.commandExamples.map((command, index) => {
                // Replace <target_domain> with the user's input if provided
                const processedCommand = targetDomain 
                  ? command.replace(/<target_domain>/g, targetDomain)
                  : command;
                
                return (
                  <div 
                    key={index} 
                    className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto relative group"
                  >
                    <pre className="text-sm font-mono pr-10">{processedCommand}</pre>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(processedCommand);
                        // Optional: Add visual feedback for copy
                        const el = document.createElement('div');
                        el.className = 'absolute top-2 right-10 bg-green-500 text-white text-xs px-2 py-1 rounded';
                        el.textContent = 'Copied!';
                        document.activeElement?.parentElement?.appendChild(el);
                        setTimeout(() => el.remove(), 2000);
                      }}
                      className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Copy to clipboard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {currentStep.guidanceNotes && (
          <div>
            <h4 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
              Guidance Notes
            </h4>
            <div className="bg-[#f7fae5] border-l-4 border-[#daed6e] p-4 rounded-r-lg">
              <p className="text-sm whitespace-pre-wrap">{currentStep.guidanceNotes}</p>
            </div>
          </div>
        )}
        
        {currentStep.requiresUserInput && (
          <div>
            <h4 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
              {currentStep.inputFieldLabel || 'Your Input'}
            </h4>
            <Input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="h-10 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder={`Enter ${currentStep.inputFieldLabel || 'your input'}`}
              type={currentStep.inputDataType === 'Number' ? 'number' : 'text'}
            />
          </div>
        )}
        
        <div className="flex justify-between pt-4">
          <Button
            onClick={handlePreviousStep}
            className="h-9 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors"
            disabled={currentStepIndex === 0}
          >
            Previous
          </Button>
          <Button
            onClick={handleNextStep}
            className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
          >
            {currentStepIndex === methodology.steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    );
  };

  // Handle back button navigation based on current path
  const handleBackNavigation = () => {
    // If we're at /methodology/guided/:methodologyId, go back to /methodology/guided
    if (location.pathname.startsWith('/methodology/guided/')) {
      navigateWithSessionParam(`/methodology/guided`);
    } else {
      // Otherwise go back to the standard path
      navigateWithSessionParam(`/guided-methodologies`);
    }
  };

  const handleSignOut = async () => {
    try {
      await authInstance.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="font-['Space_Grotesk'] text-black hidden lg:flex"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-16">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-6">
          <button 
            onClick={handleBackNavigation}
            className="text-black opacity-60 hover:opacity-100 transition-opacity"
          >
            ← Back to Guided Methodologies
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="loader"></div>
          </div>
        ) : methodology ? (
          <div className="bg-white rounded-[20px] p-8 shadow-lg">
            <div className="mb-6">
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                {methodology.name}
              </h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 mb-4">
                {methodology.description}
              </p>
              
              {/* Target Domain Input */}
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <Input
                    value={targetDomain}
                    onChange={(e) => setTargetDomain(e.target.value)}
                    className="h-9 max-w-xs bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
                    placeholder="Enter target domain (optional)"
                  />
                  <div className="text-xs text-gray-500">
                    Will replace &lt;target_domain&gt; in all commands
                  </div>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Progress</span>
                  <span className="text-sm font-medium">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-[#daed6e] h-2.5 rounded-full transition-all duration-300 ease-out" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Step indicator */}
              <div className="flex items-center mb-6 overflow-x-auto py-2">
                {methodology.steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    {index > 0 && (
                      <div className="h-[2px] w-8 bg-gray-200 mx-1"></div>
                    )}
                    <div 
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center shrink-0
                        ${index === currentStepIndex 
                          ? 'bg-[#daed6e] text-black' 
                          : completedStepIds.includes(step.id)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-400'
                        }
                      `}
                    >
                      {index + 1}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Toggle for Step-by-Step vs Visual Flow */}
            <Tabs value={viewMode} onValueChange={v => setViewMode(v as 'step')} className="mb-6">
              <TabsList>
                <TabsTrigger value="step">Step-by-Step</TabsTrigger>
              </TabsList>
              <TabsContent value="step">
                {renderCurrentStep()}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="bg-white rounded-[20px] p-8 shadow-lg">
            <div className="text-center py-8 text-gray-500">
              <p>Methodology not found</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 